<template>
  <q-card flat bordered class="q-mb-md" :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'">
    <q-card-section class="flex justify-between items-center">
      <div>
        <div class="flex items-center text-bold">
          <q-icon name="tune" class="q-mr-sm" />
          Customize Settings
        </div>
        <div class="q-mt-xs" :class="$q.dark.isActive ? 'text-grey-4' : 'text-grey-8'">
          Override the recommended settings for comp selection. Click <b>Suggest Comps</b> to get
          new comps based on these settings.
        </div>
      </div>
      <div>
        <q-btn
          no-caps
          outline
          @click="$emit('reset-settings')"
          :color="$q.dark.isActive ? 'grey-4' : 'grey-8'"
        >
          Reset Settings
        </q-btn>
      </div>
    </q-card-section>
    <q-separator />
    <q-card-section>
      <div class="row q-col-gutter-x-md q-col-gutter-y-sm">
        <q-input
          :model-value="settings.maxRadius"
          @update:model-value="(val) => updateSettingsKey('maxRadius', val)"
          no-error-icon
          dense
          outlined
          clearable
          hide-bottom-space
          label="Search Radius (mi)"
          :bg-color="$q.dark.isActive ? 'grey-8' : 'white'"
          type="number"
          :rules="[
            (val) => val === null || val >= 0 || 'Must be a positive number',
            (val) => val === null || val <= 20 || 'Must be less than 20 miles'
          ]"
          class="col-6 col-sm-4 col-md-3 col-lg"
        >
          <template #prepend>
            <q-icon name="place" size="18px" />
          </template>
        </q-input>

        <q-input
          :model-value="settings.maxMilesToUniversity"
          @update:model-value="
            (val) => updateSettingsKey('maxMilesToUniversity', val === '' ? null : val)
          "
          no-error-icon
          dense
          outlined
          clearable
          hide-bottom-space
          label="Max Miles to University"
          :bg-color="$q.dark.isActive ? 'grey-8' : 'white'"
          type="number"
          :rules="[
            (val) => val === null || val >= 0 || 'Must be a positive number',
            (val) => val === null || val <= 100 || 'Must be less than 101 miles'
          ]"
          class="col-6 col-sm-4 col-md-3 col-lg"
        >
          <template #prepend>
            <q-icon name="school" size="18px" />
          </template>
        </q-input>

        <q-input
          :model-value="settings.maxMilesToHospital"
          @update:model-value="
            (val) => updateSettingsKey('maxMilesToHospital', val === '' ? null : val)
          "
          no-error-icon
          dense
          outlined
          clearable
          hide-bottom-space
          label="Max Miles to Hospital"
          :bg-color="$q.dark.isActive ? 'grey-8' : 'white'"
          type="number"
          :rules="[
            (val) => val === null || val >= 0 || 'Must be a positive number',
            (val) => val === null || val <= 100 || 'Must be less than 101 miles'
          ]"
          class="col-6 col-sm-4 col-md-3 col-lg"
        >
          <template #prepend>
            <q-icon name="local_hospital" size="18px" />
          </template>
        </q-input>

        <q-input
          :model-value="settings.minUnits"
          @update:model-value="(val) => updateSettingsKey('minUnits', val)"
          no-error-icon
          label="Min Units"
          dense
          :bg-color="$q.dark.isActive ? 'grey-8' : 'white'"
          hide-bottom-space
          outlined
          clearable
          type="number"
          :rules="[(val) => val === null || val >= 0 || 'Must be a positive number']"
          class="col-6 col-sm-4 col-md-3 col-lg"
        >
          <template #prepend>
            <q-icon name="numbers" size="18px" />
          </template>
        </q-input>

        <q-input
          :model-value="settings.maxUnits"
          @update:model-value="(val) => updateSettingsKey('maxUnits', val)"
          no-error-icon
          label="Max Units"
          dense
          :bg-color="$q.dark.isActive ? 'grey-8' : 'white'"
          hide-bottom-space
          outlined
          clearable
          type="number"
          :rules="[(val) => val === null || val >= 0 || 'Must be a positive number']"
          class="col-6 col-sm-4 col-md-3 col-lg"
        >
          <template #prepend>
            <q-icon name="numbers" size="18px" />
          </template>
        </q-input>

        <q-input
          :model-value="settings.minYearBuilt"
          @update:model-value="(val) => updateSettingsKey('minYearBuilt', val)"
          no-error-icon
          label="Min Year Built"
          dense
          :bg-color="$q.dark.isActive ? 'grey-8' : 'white'"
          hide-bottom-space
          outlined
          clearable
          type="number"
          :rules="[
            (val) => val === null || val >= 0 || 'Must be a positive number',
            (val) => val === null || val >= 1800 || 'Must be more than 1800'
          ]"
          class="col-6 col-sm-4 col-md-3 col-lg"
        >
          <template #prepend>
            <q-icon name="event" size="18px" />
          </template>
        </q-input>

        <q-input
          :model-value="settings.maxYearBuilt"
          @update:model-value="(val) => updateSettingsKey('maxYearBuilt', val)"
          no-error-icon
          label="Max Year Built"
          dense
          :bg-color="$q.dark.isActive ? 'grey-8' : 'white'"
          hide-bottom-space
          outlined
          clearable
          type="number"
          :rules="[
            (val) => val === null || val >= 0 || 'Must be a positive number',
            (val) => val === null || val <= 2030 || 'Must be less than 2030'
          ]"
          class="col-6 col-sm-4 col-md-3 col-lg"
        >
          <template #prepend>
            <q-icon name="event" size="18px" />
          </template>
        </q-input>

        <q-input
          :model-value="settings.minNumberStories"
          @update:model-value="(val) => updateSettingsKey('minNumberStories', val)"
          no-error-icon
          label="Min Stories"
          dense
          :bg-color="$q.dark.isActive ? 'grey-8' : 'white'"
          hide-bottom-space
          outlined
          clearable
          type="number"
          :rules="[(val) => val === null || val >= 0 || 'Must be a positive number']"
          class="col-6 col-sm-4 col-md-3 col-lg"
        >
          <template #prepend>
            <q-icon name="apartment" size="18px" />
          </template>
        </q-input>

        <q-input
          :model-value="settings.maxNumberStories"
          @update:model-value="(val) => updateSettingsKey('maxNumberStories', val)"
          no-error-icon
          label="Max Stories"
          dense
          :bg-color="$q.dark.isActive ? 'grey-8' : 'white'"
          hide-bottom-space
          outlined
          clearable
          type="number"
          :rules="[(val) => val === null || val >= 0 || 'Must be a positive number']"
          class="col-6 col-sm-4 col-md-3 col-lg"
        >
          <template #prepend>
            <q-icon name="apartment" size="18px" />
          </template>
        </q-input>
      </div>

      <div class="q-mt-md">
        <div class="text-bold flex items-center">
          <div>Comp Quality</div>
          <q-badge
            outline
            :color="$q.dark.isActive ? 'grey-4' : 'primary'"
            @click="updateSettingsKey('valueAdd', 0.4)"
            class="q-px-sm q-ml-md cursor-pointer"
          >
            <q-icon name="auto_awesome" class="q-mr-sm" size="16px" />
            <span class="text-bold" style="font-size: 0.9em">Value-Add Comps</span>
          </q-badge>
        </div>
        <div class="flex items-center">
          <div class="q-mb-xs text-grey-7 q-mr-md">Lower Quality</div>
          <div class="q-mx-sm col-grow">
            <q-slider
              name="Value Add"
              :model-value="settings.valueAdd ?? 0"
              @update:model-value="(val) => updateSettingsKey('valueAdd', val)"
              snap
              label
              markers
              :label-value="renovationLabelValue(settings.valueAdd ?? 0)"
              :min="-1"
              :max="1"
              :step="0.2"
              track-size="8px"
              class="full-width"
            />
          </div>
          <div class="q-mb-xs text-grey-7 q-ml-md">Higher Quality</div>
          <q-tooltip>
            Use this slider to identify higher or lower quality comps for your analysis.
          </q-tooltip>
        </div>
      </div>

      <div class="text-bold q-mt-sm q-mb-sm">Property Type</div>
      <div class="flex q-gutter-md">
        <div
          class="col-3 q-py-sm q-px-md flex items-center justify-between"
          :class="$q.dark.isActive ? 'bg-grey-8' : 'bg-white'"
          style="border: 1px solid #e0e0e0; border-radius: 4px"
        >
          <div class="flex q-mr-xl items-center">
            <div>Affordable</div>
            <div>
              <q-icon name="info_outline" class="q-ml-sm" />
              <q-tooltip>
                Whether to exclude, include or be indifferent to affordable properties.
              </q-tooltip>
            </div>
          </div>
          <q-toggle
            :model-value="settings.isAffordable"
            @update:model-value="(val) => updateSettingsKey('isAffordable', val)"
            toggle-indeterminate
            color="primary"
            checked-icon="check"
            unchecked-icon="close"
            dense
          />
        </div>

        <div
          class="col-3 q-py-sm q-px-md flex items-center justify-between"
          :class="$q.dark.isActive ? 'bg-grey-8' : 'bg-white'"
          style="border: 1px solid #e0e0e0; border-radius: 4px"
        >
          <div class="flex q-mr-xl items-center">
            <div>Student</div>
            <div>
              <q-icon name="info_outline" class="q-ml-sm" />
              <q-tooltip>
                Whether to include only, exclude or be indifferent to student properties.
              </q-tooltip>
            </div>
          </div>
          <q-toggle
            :model-value="settings.isStudent"
            @update:model-value="(val) => updateSettingsKey('isStudent', val)"
            toggle-indeterminate
            color="primary"
            checked-icon="check"
            unchecked-icon="close"
            dense
          />
        </div>

        <div
          class="col-3 q-py-sm q-px-md flex items-center justify-between"
          :class="$q.dark.isActive ? 'bg-grey-8' : 'bg-white'"
          style="border: 1px solid #e0e0e0; border-radius: 4px"
        >
          <div class="flex q-mr-xl items-center">
            <div>Senior</div>
            <div>
              <q-icon name="info_outline" class="q-ml-sm" />
              <q-tooltip>
                Whether to include only, exclude or be indifferent to senior properties.
              </q-tooltip>
            </div>
          </div>
          <q-toggle
            :model-value="settings.isSenior"
            @update:model-value="(val) => updateSettingsKey('isSenior', val)"
            toggle-indeterminate
            color="primary"
            checked-icon="check"
            unchecked-icon="close"
            dense
          />
        </div>

        <div
          class="col-3 q-py-sm q-px-md flex items-center justify-between"
          :class="$q.dark.isActive ? 'bg-grey-8' : 'bg-white'"
          style="border: 1px solid #e0e0e0; border-radius: 4px"
        >
          <div class="flex q-mr-xl items-center">
            <div>Build to Rent</div>
            <div>
              <q-icon name="info_outline" class="q-ml-sm" />
              <q-tooltip>
                Whether to include only, exclude or be indifferent to build-to-rent properties.
              </q-tooltip>
            </div>
          </div>
          <q-toggle
            :model-value="settings.isBuildToRent"
            @update:model-value="(val) => updateSettingsKey('isBuildToRent', val)"
            toggle-indeterminate
            color="primary"
            checked-icon="check"
            unchecked-icon="close"
            dense
          />
        </div>

        <div
          v-if="isHelloDataEmployee"
          class="col-3 q-py-sm q-px-md flex items-center justify-between"
          :class="$q.dark.isActive ? 'bg-grey-8' : 'bg-white'"
          style="border: 1px solid #e0e0e0; border-radius: 4px"
        >
          <div class="flex q-mr-xl">
            Use Embedding
            <div>
              <q-icon name="info_outline" class="q-ml-sm" />
              <q-tooltip> Use embedding for similarity scoring. </q-tooltip>
            </div>
            <q-badge color="grey-7" outline class="q-ml-sm">Internal</q-badge>
          </div>
          <q-toggle
            :model-value="settings.useEmbedding ?? false"
            @update:model-value="(val) => updateSettingsKey('useEmbedding', val)"
            color="primary"
            dense
          >
            <q-tooltip> Use embedding for similarity scoring. </q-tooltip>
          </q-toggle>
        </div>
      </div>
    </q-card-section>
  </q-card>
</template>

<script lang="ts">
import { useUserStore } from '@/stores'
import { defineComponent } from 'vue'
import type { PropType } from 'vue'

export default defineComponent({
  name: 'AdvancedSettings',
  props: {
    settings: {
      type: Object as PropType<{
        maxRadius: number | null
        minUnits: number | null
        maxUnits: number | null
        minYearBuilt: number | null
        maxYearBuilt: number | null
        minNumberStories: number | null
        maxNumberStories: number | null
        valueAdd: number | null
        useEmbedding: boolean | null
        isAffordable: boolean | null
        isSenior: boolean | null
        isStudent: boolean | null
        isBuildToRent: boolean | null
        maxMilesToUniversity: number | null
        maxMilesToHospital: number | null
      }>,
      required: true
    }
  },
  emits: ['reset-settings', 'update:settings'],
  computed: {
    isHelloDataEmployee() {
      return useUserStore().isHelloDataEmployee
    }
  },
  methods: {
    updateSettingsKey(key: string, value: any) {
      this.$emit('update:settings', { ...this.settings, [key]: value })
    },
    renovationLabelValue(value: number) {
      if (value === 0) {
        return 'No Quality Change'
      } else if (value > 0) {
        return `Quality Change: +${(value * 50).toFixed(0)}%`
      } else {
        return `Quality Change: ${(value * 50).toFixed(0)}%`
      }
    }
  }
})
</script>

<style scoped></style>
